import { ref, computed, inject, type ComputedRef, type Ref, h } from 'vue'
import { useI18n } from 'vue-i18n'
import { createColumnHelper } from '@tanstack/vue-table'
import { type AxiosInstance } from 'axios'
import { useLocalStorage } from '@vueuse/core'
import { sortedIndexBy } from 'lodash-es'

import { fetchAll } from '@lib/util/network'
import { useAppsDb } from '@lib/composables/appsDb.use'
import { APPS_DB } from '@lib/constants/appsDb.const'
import { evaluateAccessor } from '@lib/util/eval'
import CTableInput from '@lib/components/CTableInput/CTableInput.vue'
import SmartFolderNameCell from '@lib/components/savedCanvases/smartFolders/SmartFolderNameCell.vue'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { User, CanvasRetrieve } from '@openapi'
import type { PitcherInfo } from '@lib/types/app'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'

interface ViewMode {
  mode: 'smartFolders' | 'normal'
}

type SmartFoldersDisplayMode = 'folders' | 'canvases'

interface PathSegment {
  value: string
  property: SmartFolderProperty
}

export const baseProperties = ['id', 'name', 'metadata', 'context', 'is_draft'] as const

export const defaultSmartFoldersAttributes = [
  { name: 'status', accessor: "item => item?.is_draft ? 'Draft' :'✅ Ready'", type: 'string' },
  { name: 'shared_link', accessor: 'item => !!item?.shared_link?.url', type: 'boolean' },
  { name: 'owned_by', accessor: 'item => item.owned_by?.name || item.owned_by?.email', type: 'string' },
  { name: 'created_at', accessor: 'created_at', type: 'date' },
  { name: 'created_by', accessor: 'item => item.created_by?.name || item.created_by?.email', type: 'string' },
  { name: 'modified_at', accessor: 'modified_at', type: 'date' },
  { name: 'modified_by', accessor: 'item => item.modified_by?.name || item.modified_by?.email', type: 'string' },
  { name: 'tags', accessor: 'tags', type: 'array' },
  { name: 'template', accessor: 'template.name', type: 'string' },
] as const

export type SmartFolderProperty = {
  name: string
  display_name?: string
  accessor: string
  type: string
  description?: string
}

export interface SmartFolderConfig {
  enabledSmartFolderProperties: SmartFolderProperty['name'][]
}

const storageKeyPrefix = 'canvas_smart-folders'

const currentPath = useLocalStorage<PathSegment[]>(`${storageKeyPrefix}_current_path`, [], {
  serializer: {
    read: (v: string) => {
      try {
        return JSON.parse(v)
      } catch {
        return []
      }
    },
    write: (v: PathSegment[]) => JSON.stringify(v),
  },
})
const pageSize = useLocalStorage(`${storageKeyPrefix}_page_size`, 20, {
  serializer: {
    read: (v: string) => parseInt(v) || 20,
    write: (v: number) => v.toString(),
  },
})
const smartFoldersDisplayMode = useLocalStorage<SmartFoldersDisplayMode>(`${storageKeyPrefix}_display_mode`, 'folders')
const activeConfiguration = ref<SmartFolderConfig | null>(null)
const isLoadingConfig = ref(false)
const isSavingConfig = ref(false)
const isFetchingCanvases = ref(false)
const currentItems = ref<CanvasRetrieve[]>([])
const page = ref(1)
const pageSizes = [10, 20, 50, 100]
const totalCount = ref(0)
const lastFetchTime = ref(0)
const searchTerm = ref('')

function isToday(date: Date): boolean {
  const today = new Date()
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  )
}

function isYesterday(date: Date): boolean {
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  )
}

function isThisWeek(date: Date): boolean {
  const today = new Date()
  const firstDayOfWeek = new Date(today.setDate(today.getDate() - today.getDay()))
  const lastDayOfWeek = new Date(firstDayOfWeek)
  lastDayOfWeek.setDate(lastDayOfWeek.getDate() + 6)
  return date >= firstDayOfWeek && date <= lastDayOfWeek
}

function isThisMonth(date: Date): boolean {
  const today = new Date()
  return date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()
}

function isThisQuarter(date: Date): boolean {
  const today = new Date()
  const quarter = Math.floor(today.getMonth() / 3)
  return Math.floor(date.getMonth() / 3) === quarter && date.getFullYear() === today.getFullYear()
}

function isThisYear(date: Date): boolean {
  return date.getFullYear() === new Date().getFullYear()
}

export default function useSmartFolders(emit?: (event: 'open', ...args: any[]) => void) {
  const { t } = useI18n()
  const { upsertEntry, getEntry } = useAppsDb()
  const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
    'pitcherInfo',
    computed(() => undefined),
  )
  const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
  const myUser = inject<Ref<User | null>>(
    'myUser',
    computed(() => null),
  )
  const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
    'pitcherSettings',
    computed(() => ({})),
  )
  const viewMode = useLocalStorage<ViewMode['mode']>(
    `canvases-view-mode-${pitcherInfo.value?.instanceId}-${myUser.value?.id}`,
    'normal',
    {
      listenToStorageChanges: true,
    },
  )
  const isSmartFoldersView = computed(() => viewMode.value === 'smartFolders')
  const isNormalView = computed(() => viewMode.value === 'normal')

  function toggleViewMode() {
    viewMode.value = viewMode.value === 'normal' ? 'smartFolders' : 'normal'
  }

  function setSmartFoldersView() {
    viewMode.value = 'smartFolders'
  }

  function setNormalView() {
    viewMode.value = 'normal'
  }

  const smartFolderRuleAttributes = computed(() => pitcherSettings.value?.smart_folder_rule_attributes?.canvas || [])

  const defaultSmartFoldersAttributesTranslations = {
    status: t('canvasUI.savedCanvases.smartFolders.status'),
    shared_link: t('canvasUI.savedCanvases.smartFolders.sharedLink'),
    owned_by: t('canvasUI.savedCanvases.smartFolders.ownedBy'),
    created_at: t('canvasUI.savedCanvases.smartFolders.createdAt'),
    created_by: t('canvasUI.savedCanvases.smartFolders.createdBy'),
    modified_at: t('canvasUI.savedCanvases.smartFolders.modifiedAt'),
    modified_by: t('canvasUI.savedCanvases.smartFolders.modifiedBy'),
    tags: t('canvasUI.savedCanvases.smartFolders.tags'),
    template: t('canvasUI.savedCanvases.smartFolders.template'),
  } as const
  const defaultSmartFoldersAttributesTranslated = defaultSmartFoldersAttributes.map((a) => ({
    ...a,
    display_name: defaultSmartFoldersAttributesTranslations[a.name],
  }))

  const allSmartFolderProperties = computed(() => [
    ...defaultSmartFoldersAttributesTranslated,
    ...smartFolderRuleAttributes.value,
  ])

  const enabledSmartFolderProperties = computed(() => {
    if (!activeConfiguration.value) return []

    return activeConfiguration.value.enabledSmartFolderProperties.reduce((acc, name) => {
      const property = allSmartFolderProperties.value.find((prop) => prop.name === name)
      if (property) {
        const insertIndex = sortedIndexBy(acc, property, (prop) =>
          activeConfiguration.value?.enabledSmartFolderProperties.indexOf(prop.name),
        )
        acc.splice(insertIndex, 0, property)
      }
      return acc
    }, [] as SmartFolderProperty[])
  })

  function getDateGroup(date: Date): string {
    if (isNaN(date.getTime())) return t('canvasUI.smartFolders.invalidDate')
    if (isToday(date)) return t('canvasUI.smartFolders.today')
    if (isYesterday(date)) return t('canvasUI.smartFolders.yesterday')
    if (isThisWeek(date)) return t('canvasUI.smartFolders.thisWeek')
    if (isThisMonth(date)) return t('canvasUI.smartFolders.thisMonth')
    if (isThisQuarter(date)) return t('canvasUI.smartFolders.thisQuarter')
    if (isThisYear(date)) return t('canvasUI.smartFolders.thisYear')
    return t('canvasUI.smartFolders.olderThanAYear')
  }

  const groupings = computed(() => {
    let filteredCanvases = currentItems.value

    const result = enabledSmartFolderProperties.value.reduce(
      (acc, property, index) => {
        const { name, type, accessor } = property
        acc[name] = {}

        filteredCanvases.forEach((canvas) => {
          let value = evaluateAccessor(canvas, accessor) as any

          let boolGroup
          let date
          let dateGroup
          switch (type) {
            case 'boolean':
              boolGroup =
                value === null
                  ? t('canvasUI.smartFolders.none')
                  : value
                    ? t('canvasUI.smartFolders.yes')
                    : t('canvasUI.smartFolders.no')
              if (!acc[name][boolGroup]) acc[name][boolGroup] = { items: [], count: 0 }
              acc[name][boolGroup].items.push(canvas)
              acc[name][boolGroup].count++
              break

            case 'date':
              date = new Date(value as any)
              dateGroup = getDateGroup(date)
              if (!acc[name][dateGroup]) acc[name][dateGroup] = { items: [], count: 0 }
              acc[name][dateGroup].items.push(canvas)
              acc[name][dateGroup].count++
              break

            case 'array':
              if (Array.isArray(value)) {
                value.forEach((item) => {
                  const arrayItem = item || t('canvasUI.smartFolders.none')
                  if (!acc[name][arrayItem]) acc[name][arrayItem] = { items: [], count: 0 }
                  acc[name][arrayItem].items.push(canvas)
                  acc[name][arrayItem].count++
                })
              } else {
                const arrayItem = value || t('canvasUI.smartFolders.none')
                if (!acc[name][arrayItem]) acc[name][arrayItem] = { items: [], count: 0 }
                acc[name][arrayItem].items.push(canvas)
                acc[name][arrayItem].count++
              }
              break

            case 'string':
            default:
              value = value || t('canvasUI.smartFolders.none')
              if (!acc[name][value]) acc[name][value] = { items: [], count: 0 }
              acc[name][value].items.push(canvas)
              acc[name][value].count++
              break
          }
        })

        if (Object.keys(acc[name]).length === 0) {
          acc[name][t('canvasUI.smartFolders.none')] = { items: [], count: 0 }
        }

        if (index < enabledSmartFolderProperties.value.length - 1) {
          const selectedGroup = currentPath.value[index]
          if (selectedGroup && acc[name][selectedGroup.value]) {
            filteredCanvases = acc[name][selectedGroup.value].items
          } else {
            filteredCanvases = []
          }
        }

        return acc
      },
      {} as Record<string, Record<string, { items: CanvasRetrieve[]; count: number }>>,
    )

    return result
  })

  const currentGroupingData = computed(() => {
    let items = filteredItems.value

    if (smartFoldersDisplayMode.value === 'folders' && searchTerm.value) {
      const term = searchTerm.value.toLowerCase()

      items = items.filter((item) => {
        return (
          enabledSmartFolderProperties.value.some((property) => {
            const value = evaluateAccessor(item, property.accessor)
            if (value == null) return false
            if (Array.isArray(value)) {
              return value.some((v) => String(v).toLowerCase().includes(term))
            } else if (property.type === 'boolean') {
              const boolValue = value ? t('canvasUI.smartFolders.yes') : t('canvasUI.smartFolders.no')
              return boolValue.toLowerCase().includes(term)
            } else if (property.type === 'date') {
              const dateValue = typeof value === 'boolean' ? null : new Date(value)
              const dateGroup = dateValue ? getDateGroup(dateValue) : t('canvasUI.smartFolders.none')
              return dateGroup.toLowerCase().includes(term)
            }

            return String(value).toLowerCase().includes(term)
          }) || item.name.toLowerCase().includes(term)
        )
      })
    }

    if (
      smartFoldersDisplayMode.value === 'canvases' ||
      currentPath.value.length === enabledSmartFolderProperties.value.length
    ) {
      // For canvas view, apply search across all enabled properties
      if (searchTerm.value) {
        const term = searchTerm.value.toLowerCase()
        return items.filter((canvas) => {
          return (
            enabledSmartFolderProperties.value.some((property) => {
              const value = evaluateAccessor(canvas, property.accessor)
              if (value == null) return false

              if (Array.isArray(value)) {
                return value.some((v) => String(v).toLowerCase().includes(term))
              }

              return String(value).toLowerCase().includes(term)
            }) || canvas.name.toLowerCase().includes(term)
          ) // Always include name search
        })
      }
      return items
    } else {
      const currentProperty = enabledSmartFolderProperties.value[currentPath.value.length]
      const groups = items.reduce(
        (accumulator, item) => {
          let value = evaluateAccessor(item, currentProperty?.accessor) as any

          if (value === undefined || value === null) {
            value = t('canvasUI.smartFolders.none')
          } else if (currentProperty.type === 'boolean') {
            value = value ? t('canvasUI.smartFolders.yes') : t('canvasUI.smartFolders.no')
          } else if (currentProperty.type === 'date') {
            value = getDateGroup(new Date(value))
          }

          if (currentProperty?.type === 'array' && Array.isArray(value)) {
            value.forEach((val) => {
              if (!accumulator[val]) {
                accumulator[val] = { name: val, itemCount: 0 }
              }
              accumulator[val].itemCount += 1
            })
          } else {
            if (!accumulator[value]) {
              accumulator[value] = { name: value, itemCount: 0 }
            }
            accumulator[value].itemCount += 1
          }

          return accumulator
        },
        {} as Record<string, { name: string; itemCount: number }>,
      )

      return Object.values(groups)
    }
  })

  function onRowClick(row: any, e?: MouseEvent) {
    if (smartFoldersDisplayMode.value === 'folders') {
      const currentProperty = enabledSmartFolderProperties.value[currentPath.value.length]
      if (currentProperty) {
        currentPath.value = [...currentPath.value, { value: row.name, property: currentProperty }]

        if (currentPath.value.length === enabledSmartFolderProperties.value.length) {
          smartFoldersDisplayMode.value = 'canvases'
        }
      }
    } else {
      emit?.('open', e, row)
    }
  }

  const breadcrumbItems = computed(() => {
    if (enabledSmartFolderProperties.value.length === 0) {
      return []
    }
    return [
      {
        label: enabledSmartFolderProperties.value[0].display_name || enabledSmartFolderProperties.value[0].name,
        path: [],
      },
      ...currentPath.value.map((segment, index) => ({
        label: `${segment.property.display_name || segment.property.name}: ${segment.value}`,
        path: currentPath.value.slice(0, index + 1),
      })),
    ]
  })

  function onBreadcrumbClick(item: { label: string; path: PathSegment[] }) {
    currentPath.value = item.path
    smartFoldersDisplayMode.value = 'folders'
  }

  const filteredItems = computed(() => {
    const items = currentItems.value

    // Apply search filter if search term exists
    if (searchTerm.value) {
      const term = searchTerm.value.toLowerCase()

      return items.filter((item) => {
        // Search in all enabled properties
        return (
          enabledSmartFolderProperties.value.some((property) => {
            const value = evaluateAccessor(item, property.accessor)
            if (value == null) return false

            if (Array.isArray(value)) {
              return value.some((v) => String(v).toLowerCase().includes(term))
            }

            return String(value).toLowerCase().includes(term)
          }) ||
          // Always include name search
          item.name.toLowerCase().includes(term)
        )
      })
    }

    // If no search term, apply normal path filtering
    return items.filter((item) => {
      return currentPath.value.every((segment) => {
        const { value, property } = segment
        let itemValue = evaluateAccessor(item, property.accessor) as any

        if (itemValue === undefined || itemValue === null) {
          itemValue = t('canvasUI.smartFolders.none')
        } else if (property.type === 'boolean') {
          itemValue = itemValue ? t('canvasUI.smartFolders.yes') : t('canvasUI.smartFolders.no')
        } else if (property.type === 'date') {
          itemValue = getDateGroup(new Date(itemValue))
        }

        if (property.type === 'array' && Array.isArray(itemValue)) {
          return itemValue.includes(value)
        }

        return itemValue === value
      })
    })
  })

  const displayItems = computed(() => {
    if (smartFoldersDisplayMode.value === 'folders') {
      return currentGroupingData.value
    } else {
      return filteredItems.value
    }
  })

  const columnHelper = createColumnHelper<CanvasRetrieve>()

  function createDynamicColumns() {
    return [
      columnHelper.accessor((row) => row.name, {
        id: 'name',
        cell: (cellCtx) =>
          h(CTableInput, {
            modelValue: cellCtx.getValue(),
            state: 'view',
            readonly: true,
            class: 'cursor-pointer',
            valueClass: 'font-700 underline underline-solid',
            onClick: () => onRowClick(cellCtx.row.original),
          }),
        header: t('canvasUI.components.templates.CTemplateManagement.nameTh'),
      }),
      ...enabledSmartFolderProperties.value.map((prop) =>
        columnHelper.accessor((row) => evaluateAccessor(row, prop.accessor), {
          id: prop.name,
          header: () => h('div', { class: 'min-w-[100px]' }, prop.display_name || prop.name),
          cell: (info) => {
            const value = info.getValue()
            if (prop.type === 'boolean') {
              return h('span', {}, value ? t('canvasUI.smartFolders.yes') : t('canvasUI.smartFolders.no'))
            }
            return h('div', {}, value ?? '')
          },
        }),
      ),
    ]
  }

  const columns = computed(() => {
    if (smartFoldersDisplayMode.value === 'folders') {
      const currentGroupingProperty = enabledSmartFolderProperties.value[currentPath.value.length]
      return [
        columnHelper.accessor('name', {
          id: 'name',
          header: () =>
            h('div', {}, [
              t('canvasUI.smartFolders.folderName'),
              ' (',
              currentGroupingProperty.display_name || currentGroupingProperty.name,
              ')',
            ]),
          cell: (info) =>
            h(
              SmartFolderNameCell,
              {
                onClick: () => onRowClick(info.row.original),
                name: info.row.original.name,
              },
              {
                default: () => info.getValue(),
              },
            ),
        }),
        columnHelper.accessor('itemCount' as any, {
          id: 'itemCount',
          header: t('canvasUI.smartFolders.itemCount'),
          cell: (info) => info.getValue(),
        }),
      ]
    } else {
      return createDynamicColumns()
    }
  })

  function toggleSmartFoldersDisplayMode() {
    smartFoldersDisplayMode.value = smartFoldersDisplayMode.value === 'folders' ? 'canvases' : 'folders'
  }

  function createGroupings(canvases: CanvasRetrieve[], properties: SmartFolderProperty[]): Record<string, any> {
    if (properties.length === 0) return canvases

    const [currentProperty, ...remainingProperties] = properties
    const { name, type, accessor } = currentProperty
    const groupings: Record<string, any> = {}

    const groupHandlers = {
      boolean: (value: any) =>
        value === null
          ? t('canvasUI.smartFolders.none')
          : value
            ? t('canvasUI.smartFolders.yes')
            : t('canvasUI.smartFolders.no'),
      date: (value: any) => getDateGroup(new Date(value)),
      string: (value: any) => value || t('canvasUI.smartFolders.none'),
      array: (value: any) => (Array.isArray(value) ? value.join(', ') : value) || t('canvasUI.smartFolders.none'),
      default: () => t('canvasUI.smartFolders.ungrouped'),
    }

    const getGroupKey = (value: any) =>
      (groupHandlers[type as keyof typeof groupHandlers] || groupHandlers.default)(value)

    canvases.forEach((canvas) => {
      const value = evaluateAccessor(canvas, accessor)
      const groupKey = getGroupKey(value)

      if (!groupings[groupKey]) {
        groupings[groupKey] = remainingProperties.length === 0 ? [] : {}
      }

      if (remainingProperties.length === 0) {
        groupings[groupKey].push(canvas)
      } else {
        const subGroupings = createGroupings([canvas], remainingProperties)
        Object.assign(groupings[groupKey], subGroupings)
      }
    })

    return { [name]: groupings }
  }

  const MAX_FETCH_LIMIT = pitcherSettings.value?.smart_folder_rule_attributes?.max_sync_limit

  async function fetchCanvases(params?: {
    ownerId?: User['id']
    isAnyTypeOfAdmin?: boolean
  }): Promise<CanvasRetrieve[]> {
    if (!restApiAxios?.value) {
      throw new Error('REST API axios was not provided')
    }
    const reqParams = {
      sync: pitcherSettings.value?.smart_folder_rule_attributes?.sync ?? true,
      instance_id: pitcherInfo.value?.instanceId,
      is_section: false,
      is_template: false,
      ...(params?.isAnyTypeOfAdmin ? { owned_by__id: params.ownerId } : {}),
      fields: [...baseProperties, ...defaultSmartFoldersAttributes.map((a) => a.name)].join(','),
      page_size: Math.min(pitcherSettings.value?.smart_folder_rule_attributes?.sync_chunk_size || 300, 500),
    }

    const results = await fetchAll<CanvasRetrieve>(
      (params: any) => restApiAxios.value!.get('canvases/', { params }),
      reqParams,
      MAX_FETCH_LIMIT,
    )
    return results
  }

  async function getLocalCanvases(): Promise<CanvasRetrieve[] | null> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SmartFoldersDB', 1)
      request.onerror = () => reject(new Error('Failed to open database'))
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['canvases'], 'readonly')
        const store = transaction.objectStore('canvases')
        const getRequest = store.get('canvases')
        getRequest.onerror = () => reject(new Error('Failed to retrieve canvases'))
        getRequest.onsuccess = () => resolve(getRequest.result || null)
      }
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        db.createObjectStore('canvases')
      }
    })
  }

  async function saveLocalCanvases(canvases: CanvasRetrieve[]): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SmartFoldersDB', 1)
      request.onerror = () => reject(new Error('Failed to open database'))
      request.onsuccess = () => {
        const db = request.result
        const transaction = db.transaction(['canvases'], 'readwrite')
        const store = transaction.objectStore('canvases')
        const putRequest = store.put(canvases, 'canvases')
        putRequest.onerror = () => reject(new Error('Failed to save canvases'))
        putRequest.onsuccess = () => resolve()
      }
    })
  }

  async function fetchData(params?: { ownerId?: User['id']; isAnyTypeOfAdmin?: boolean }) {
    isFetchingCanvases.value = true
    try {
      const now = Date.now()
      const localData = await getLocalCanvases()
      if (localData) {
        currentItems.value = localData
      }
      const hasFetchedInLast5Min = now - lastFetchTime.value <= 5 * 60 * 1000
      if (!hasFetchedInLast5Min || params?.ownerId) {
        // 5 minutes
        const fetchedItems = await fetchCanvases(params)
        currentItems.value = fetchedItems
        await saveLocalCanvases(fetchedItems)
        lastFetchTime.value = now
      }

      totalCount.value = currentItems.value.length
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      isFetchingCanvases.value = false
    }
  }

  function resetPath() {
    currentPath.value = []
    smartFoldersDisplayMode.value = 'folders'
  }

  function onTableChange(
    event: 'pageChanged' | 'pageSizeChanged' | 'sortChanged',
    value: number | { pageIndex: number; pageSize: number } | { id: string; desc: boolean },
  ) {
    switch (event) {
      case 'pageChanged':
        page.value = (value as { pageIndex: number }).pageIndex + 1
        break
      case 'pageSizeChanged':
        pageSize.value = value as number
        page.value = 1
        break
      case 'sortChanged':
        // Implement sorting logic
        break
    }
  }

  async function saveConfiguration(config: SmartFolderConfig) {
    isSavingConfig.value = true
    try {
      if (!myUser.value) throw new Error('myUser could not be injected')
      await upsertEntry({
        store_name: APPS_DB.STORES.SMART_FOLDERS,
        user_id: myUser.value.id,
        entry_key: APPS_DB.ENTRIES.CONFIGURATION,
        data: config,
      })
      activeConfiguration.value = config
      resetPath() // Reset breadcrumbs after saving configuration
    } catch (error) {
      console.error('Error saving configuration:', error)
    } finally {
      isSavingConfig.value = false
    }
  }

  async function loadConfiguration(): Promise<void> {
    isLoadingConfig.value = true
    try {
      if (!myUser.value) throw new Error('myUser could not be injected')
      const entry = (await getEntry({
        store_name: APPS_DB.STORES.SMART_FOLDERS,
        user_id: myUser.value.id,
        entry_key: APPS_DB.ENTRIES.CONFIGURATION,
      })) as unknown as { value: SmartFolderConfig | null }

      if (
        entry.value &&
        typeof entry.value === 'object' &&
        'enabledSmartFolderProperties' in entry.value &&
        Array.isArray(entry.value.enabledSmartFolderProperties) &&
        entry.value.enabledSmartFolderProperties.every((item) => typeof item === 'string')
      ) {
        // Validate that entry.value is an object with enabledSmartFolderProperties as an array of strings
        activeConfiguration.value = entry.value
      } else {
        console.error('Invalid configuration format - resetting to default')
        activeConfiguration.value = { enabledSmartFolderProperties: [] }
      }
    } catch (error) {
      console.error('Error loading configuration:', error)
    } finally {
      isLoadingConfig.value = false
    }
  }

  function handleSearch(term: string) {
    searchTerm.value = term
    if (term) {
      currentPath.value = []
    }
  }

  return {
    currentGroupingData,
    groupings,
    viewMode,
    smartFoldersDisplayMode,
    activeConfiguration,
    isLoadingConfig,
    isSavingConfig,
    isSmartFoldersView,
    isNormalView,
    isFetchingCanvases,
    currentItems,
    displayItems,
    breadcrumbItems,
    columns,
    page,
    pageSize,
    pageSizes,
    totalCount,
    allSmartFolderProperties,
    enabledSmartFolderProperties,
    resetPath,
    toggleSmartFoldersDisplayMode,
    onBreadcrumbClick,
    onRowClick,
    fetchData,
    onTableChange,
    saveConfiguration,
    loadConfiguration,
    createGroupings,
    toggleViewMode,
    setSmartFoldersView,
    setNormalView,
    searchTerm,
    handleSearch,
  }
}
